import React from 'react';
import { Link, type Href } from 'expo-router';
import { Platform, StyleSheet, Text, View } from 'react-native';

import { interRegular, interSemiBold } from '../../lib/fonts';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { type LocalizedMessage, resolveMessage } from '../../lib/i18n/messages';

const DOCS_BASE = 'https://docs.paperzilla.ai';

type WorkflowTone = 'direct' | 'documented' | 'conditional' | 'bridge';

interface WorkflowCellData {
    channel: string;
    description: LocalizedMessage;
    href: Href;
    label: LocalizedMessage;
    tone: WorkflowTone;
}

interface WorkflowRowData {
    access: LocalizedMessage;
    agent: string;
    cells: WorkflowCellData[];
}

const workflowRows: WorkflowRowData[] = [
    {
        agent: 'OpenClaw',
        access: msg({ id: "agents.agentworkflowmatrix.paperzillaSkillPzCli", message: "Paperzilla skill + pz CLI", comment: "UI access in components/agents/AgentWorkflowMatrix.tsx" }),
        cells: [
            {
                channel: 'Telegram',
                label: msg({ id: "agents.agentworkflowmatrix.directWhenConfigured", message: "Direct when configured", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'direct',
                description: msg({ id: "agents.agentworkflowmatrix.chatWithOpenclawScheduleAWeekdayCronJobAndHaveOpenclawDeliverTheBrief", message: "Chat with OpenClaw, schedule a weekday cron job, and have OpenClaw deliver the brief.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/guides/openclaw-telegram-briefs`,
            },
            {
                channel: 'Slack',
                label: msg({ id: "agents.agentworkflowmatrix.directWhenConfigured", message: "Direct when configured", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'direct',
                description: msg({ id: "agents.agentworkflowmatrix.useTheSameCliAndSchedulingPatternWithAnOpenclawSlackChannel", message: "Use the same CLI and scheduling pattern with an OpenClaw Slack channel.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-openclaw-slack`,
            },
            {
                channel: 'Microsoft Teams',
                label: msg({ id: "agents.agentworkflowmatrix.setupRequired", message: "Setup required", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'conditional',
                description: msg({ id: "agents.agentworkflowmatrix.availableAfterTheTeamsChannelOrPluginIsConfiguredTheSetupHasMoreSteps", message: "Available after the Teams channel or plugin is configured. The setup has more steps.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-openclaw-teams`,
            },
        ],
    },
    {
        agent: 'Claude + Cowork',
        access: msg({ id: "agents.agentworkflowmatrix.paperzillaMcp", message: "Paperzilla MCP", comment: "UI access in components/agents/AgentWorkflowMatrix.tsx" }),
        cells: [
            {
                channel: 'Telegram',
                label: msg({ id: "agents.agentworkflowmatrix.bridgeRequired", message: "Bridge required", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'bridge',
                description: msg({ id: "agents.agentworkflowmatrix.claudeCanPrepareTheBriefClaudeCodeChannelsOpenclawOrAnotherBridgeSendsItToTelegr", message: "Claude can prepare the brief. Claude Code Channels, OpenClaw, or another bridge sends it to Telegram.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-claude-cowork-telegram`,
            },
            {
                channel: 'Slack',
                label: msg({ id: "agents.agentworkflowmatrix.documentedWorkflow", message: "Documented workflow", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'documented',
                description: msg({ id: "agents.agentworkflowmatrix.discussPapersInSlackCoworkCanScheduleADraftForReviewBeforeItIsSent", message: "Discuss papers in Slack. Cowork can schedule a draft for review before it is sent.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/guides/claude-slack-briefs`,
            },
            {
                channel: 'Microsoft Teams',
                label: msg({ id: "agents.agentworkflowmatrix.connectorRequired", message: "Connector required", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'conditional',
                description: msg({ id: "agents.agentworkflowmatrix.useMicrosoft365ContextToPrepareATeamsBriefDirectDeliveryNeedsAConnector", message: "Use Microsoft 365 context to prepare a Teams brief. Direct delivery needs a connector.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-claude-cowork-teams`,
            },
        ],
    },
    {
        agent: 'Codex',
        access: msg({ id: "agents.agentworkflowmatrix.paperzillaMcpOrPzCli", message: "Paperzilla MCP or pz CLI", comment: "UI access in components/agents/AgentWorkflowMatrix.tsx" }),
        cells: [
            {
                channel: 'Telegram',
                label: msg({ id: "agents.agentworkflowmatrix.bridgeRequired", message: "Bridge required", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'bridge',
                description: msg({ id: "agents.agentworkflowmatrix.createATelegramReadyBriefThenSendItManuallyOrThroughYourOwnAutomationOrOpenclaw", message: "Create a Telegram-ready brief, then send it manually or through your own automation or OpenClaw.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-codex-telegram`,
            },
            {
                channel: 'Slack',
                label: msg({ id: "agents.agentworkflowmatrix.setupRequired", message: "Setup required", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'conditional',
                description: msg({ id: "agents.agentworkflowmatrix.haveCodexPrepareTheBriefConfirmThatASlackStartedTaskCanReachPaperzillaBeforeSche", message: "Have Codex prepare the brief. Confirm that a Slack-started task can reach Paperzilla before scheduling it.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/answers/research-brief-codex-slack`,
            },
            {
                channel: 'Microsoft Teams',
                label: msg({ id: "agents.agentworkflowmatrix.draftWorkflow", message: "Draft workflow", comment: "UI label in components/agents/AgentWorkflowMatrix.tsx" }),
                tone: 'documented',
                description: msg({ id: "agents.agentworkflowmatrix.useACodexAutomationToPutATeamsDraftInTriageReviewItBeforePosting", message: "Use a Codex automation to put a Teams draft in Triage. Review it before posting.", comment: "UI description in components/agents/AgentWorkflowMatrix.tsx" }),
                href: `${DOCS_BASE}/guides/codex-teams-briefs`,
            },
        ],
    },
];

export default function AgentWorkflowMatrix({
    fontsLoaded,
    stacked,
}: {
    fontsLoaded: boolean;
    stacked: boolean;
}) {
    const { i18n } = useLingui();
    return (
        <View style={styles.matrix}>
            <View style={[styles.headerRow, stacked && styles.headerRowHidden]}>
                <Text style={[styles.headerCell, styles.agentHeader, fontsLoaded && interSemiBold]}><Trans id="agents.agentworkflowmatrix.agentLayer" comment="UI copy in components/agents/AgentWorkflowMatrix.tsx">
                    Agent layer
                </Trans></Text>
                {['Telegram', 'Slack', 'Microsoft Teams'].map((channel) => (
                    <Text key={channel} style={[styles.headerCell, fontsLoaded && interSemiBold]}>
                        {channel}
                    </Text>
                ))}
            </View>

            {workflowRows.map((row) => (
                <View key={row.agent} style={[styles.row, stacked && styles.rowStacked]}>
                    <View style={[styles.agentCell, stacked && styles.agentCellStacked]}>
                        <Text style={[styles.agentName, stacked && styles.agentNameStacked, fontsLoaded && interSemiBold]}>
                            {row.agent}
                        </Text>
                        <Text style={[styles.agentAccess, stacked && styles.agentAccessStacked, fontsLoaded && interRegular]}>
                            {resolveMessage(i18n, row.access)}
                        </Text>
                    </View>
                    {row.cells.map((cell) => (
                        <View key={cell.channel} style={[styles.workflowCell, stacked && styles.workflowCellStacked]}>
                            <Text
                                style={[
                                    styles.mobileChannel,
                                    !stacked && styles.mobileChannelHidden,
                                    fontsLoaded && interSemiBold,
                                ]}
                            >
                                {cell.channel}
                            </Text>
                            <View style={[styles.statusPill, statusPillStyles[cell.tone]]}>
                                <Text style={[styles.statusText, statusTextStyles[cell.tone], fontsLoaded && interSemiBold]}>
                                    {resolveMessage(i18n, cell.label)}
                                </Text>
                            </View>
                            <Text style={[styles.cellDescription, fontsLoaded && interRegular]}>
                                {resolveMessage(i18n, cell.description)}
                            </Text>
                            <Link
                                href={cell.href}
                                target="_blank"
                                style={[styles.cellLink, fontsLoaded && interSemiBold]}
                            ><Trans id="agents.agentworkflowmatrix.workflowDetails" comment="UI copy in components/agents/AgentWorkflowMatrix.tsx">
                                Workflow details ↗
                            </Trans></Link>
                        </View>
                    ))}
                </View>
            ))}
        </View>
    );
}

const statusPillStyles: Record<WorkflowTone, object> = {
    direct: { backgroundColor: '#dff1d5', borderColor: '#9fc78a' },
    documented: { backgroundColor: '#dcecf0', borderColor: '#9fc3cb' },
    conditional: { backgroundColor: '#f5e8c9', borderColor: '#dac185' },
    bridge: { backgroundColor: '#ece9e0', borderColor: '#cbc5b7' },
};

const statusTextStyles: Record<WorkflowTone, object> = {
    direct: { color: '#315b2e' },
    documented: { color: '#275564' },
    conditional: { color: '#71551c' },
    bridge: { color: '#625d52' },
};

const matrixShadow = Platform.select({
    web: { boxShadow: '0 18px 50px rgba(31, 42, 30, 0.08)' },
    default: {
        elevation: 3,
        shadowColor: '#1f2a1e',
        shadowOffset: { width: 0, height: 12 },
        shadowOpacity: 0.08,
        shadowRadius: 22,
    },
});

const styles = StyleSheet.create({
    matrix: {
        backgroundColor: '#fffdf8',
        borderColor: '#d8d3c7',
        borderRadius: 18,
        borderWidth: 1,
        overflow: 'hidden',
        ...matrixShadow,
    },
    headerRow: {
        backgroundColor: '#203526',
        flexDirection: 'row',
        paddingHorizontal: 10,
    },
    headerRowHidden: {
        display: 'none',
    },
    headerCell: {
        color: '#f7f4ec',
        flex: 1,
        fontSize: 12,
        fontWeight: '600',
        letterSpacing: 0.6,
        paddingHorizontal: 14,
        paddingVertical: 14,
        textTransform: 'uppercase',
    },
    agentHeader: {
        flexBasis: 170,
        flexGrow: 0,
    },
    row: {
        borderTopColor: '#ddd8cc',
        borderTopWidth: 1,
        flexDirection: 'row',
        paddingHorizontal: 10,
    },
    rowStacked: {
        flexDirection: 'column',
        paddingHorizontal: 0,
    },
    agentCell: {
        flexBasis: 170,
        flexGrow: 0,
        justifyContent: 'center',
        paddingHorizontal: 14,
        paddingVertical: 22,
    },
    agentCellStacked: {
        backgroundColor: '#203526',
        flexBasis: 'auto',
        paddingHorizontal: 20,
        paddingVertical: 18,
    },
    agentName: {
        color: '#18271c',
        fontSize: 17,
        fontWeight: '600',
        lineHeight: 22,
    },
    agentNameStacked: {
        color: '#fffdf8',
    },
    agentAccess: {
        color: '#6a7169',
        fontSize: 12,
        lineHeight: 18,
        marginTop: 5,
    },
    agentAccessStacked: {
        color: '#bac9bb',
    },
    workflowCell: {
        borderLeftColor: '#e5e0d5',
        borderLeftWidth: 1,
        flex: 1,
        minWidth: 0,
        paddingHorizontal: 14,
        paddingVertical: 20,
    },
    workflowCellStacked: {
        borderLeftWidth: 0,
        borderTopColor: '#e5e0d5',
        borderTopWidth: 1,
        paddingHorizontal: 20,
        paddingVertical: 20,
    },
    mobileChannel: {
        color: '#18271c',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 10,
    },
    mobileChannelHidden: {
        display: 'none',
    },
    statusPill: {
        alignSelf: 'flex-start',
        borderRadius: 999,
        borderWidth: 1,
        paddingHorizontal: 9,
        paddingVertical: 4,
    },
    statusText: {
        fontSize: 11,
        fontWeight: '600',
        letterSpacing: 0.15,
        lineHeight: 14,
    },
    cellDescription: {
        color: '#596158',
        fontSize: 13,
        lineHeight: 20,
        marginTop: 11,
    },
    cellLink: {
        color: '#41733e',
        fontSize: 12,
        fontWeight: '600',
        marginTop: 14,
        textDecorationLine: 'none',
    },
});
